// StatusLeiste.jsx — Turnier-Status als Ablauf mit dem nächsten Schritt als
// Knopf (18.10.2026). Ersetzt die zwei Felder „Status“ und „Sichtbarkeit“:
// sichtbar ist ein Turnier ab „Anmeldung offen“, das ergibt sich aus dem Status.
//
// Der Server prüft jeden Wechsel (services/turnierStatusWechsel.js). Lehnt er
// ab, steht hier, was fehlt (Vorbereitungsstand), statt eines window.confirm.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ABLAUF, TURNIER_STATUS, statusText, statusWechseln } from '../../lib/turnierStatus';
import '../../styles/TurnierAblauf.css';

/**
 * @param {object} props
 * @param {number} props.turnierId
 * @param {string} props.status         gespeicherter Status
 * @param {string} [props.statusWirksam] vom Server (Frist vorbei → geschlossen)
 * @param {boolean} [props.fristVorbei]
 * @param {(neu: {status:string, veroeffentlicht:number}) => void} [props.onGeaendert]
 * @param {() => Promise<boolean>} [props.vorher]  z. B. erst speichern; false = abbrechen
 */
export default function StatusLeiste({ turnierId, status, statusWirksam, fristVorbei, uebung, onGeaendert, vorher }) {
  const navigate = useNavigate();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null); // { error, fehlend }
  const [ok, setOk] = useState('');
  const wirksam = statusWirksam || status;
  const abgesagt = status === TURNIER_STATUS.ABGESAGT;
  const index = ABLAUF.indexOf(wirksam);

  const wechseln = async (ziel, frage) => {
    if (frage && !window.confirm(frage)) return;
    setFehler(null); setOk('');
    if (vorher && !(await vorher())) return;
    setLaeuft(true);
    const erg = await statusWechseln(turnierId, ziel);
    setLaeuft(false);
    if (!erg.ok) { setFehler(erg); return; }
    setOk(`Status: ${statusText(erg.data?.status)}`);
    onGeaendert && onGeaendert(erg.data);
  };

  // Der nächste Schritt und was sonst noch geht (Rückwege)
  let naechster = null;
  const weitere = [];
  if (abgesagt) {
    naechster = { text: 'Wieder als Entwurf aufnehmen', ziel: TURNIER_STATUS.ENTWURF };
  } else if (wirksam === TURNIER_STATUS.ENTWURF && uebung) {
    // Übungsturnier: direkt starten, bleibt unveröffentlicht
    naechster = { text: 'Übung starten', ziel: TURNIER_STATUS.LAUFEND, hinweis: 'Übungsturnier: bleibt unveröffentlicht, keine Mails, kein Abgleich mit der Dojosoftware.' };
  } else if (wirksam === TURNIER_STATUS.ENTWURF) {
    naechster = { text: 'Anmeldung öffnen', ziel: TURNIER_STATUS.AKTIV, hinweis: 'Danach ist das Turnier öffentlich sichtbar und Vereine können melden.' };
  } else if (wirksam === TURNIER_STATUS.AKTIV) {
    naechster = { text: 'Anmeldung schließen', ziel: TURNIER_STATUS.ANMELDUNG_GESCHLOSSEN };
    weitere.push({ text: 'Zurück zum Entwurf', ziel: TURNIER_STATUS.ENTWURF, frage: 'Zurück zum Entwurf? Das Turnier ist dann nicht mehr öffentlich sichtbar.' });
  } else if (wirksam === TURNIER_STATUS.ANMELDUNG_GESCHLOSSEN) {
    naechster = { text: 'Turnier starten', ziel: TURNIER_STATUS.LAUFEND };
    if (!fristVorbei) weitere.push({ text: 'Anmeldung wieder öffnen', ziel: TURNIER_STATUS.AKTIV });
    weitere.push({ text: 'Zurück zum Entwurf', ziel: TURNIER_STATUS.ENTWURF, frage: 'Zurück zum Entwurf? Das Turnier ist dann nicht mehr öffentlich sichtbar.' });
  } else if (wirksam === TURNIER_STATUS.LAUFEND) {
    naechster = { text: 'Turnier abschließen …', nav: '/dashboard/ergebnisse', hinweis: 'Abgeschlossen wird unter „Ergebnisse“: dort steht, was noch offen ist.' };
    if (uebung) weitere.push({ text: 'Übung zurück auf Entwurf', ziel: TURNIER_STATUS.ENTWURF });
  } else if (wirksam === TURNIER_STATUS.ABGESCHLOSSEN) {
    naechster = { text: 'Wieder öffnen …', nav: '/dashboard/ergebnisse', hinweis: 'Wieder öffnen geht nur mit Begründung unter „Ergebnisse“.' };
  }
  if (!abgesagt && [TURNIER_STATUS.ENTWURF, TURNIER_STATUS.AKTIV, TURNIER_STATUS.ANMELDUNG_GESCHLOSSEN].includes(wirksam)) {
    weitere.push({ text: 'Turnier absagen', ziel: TURNIER_STATUS.ABGESAGT, frage: 'Turnier wirklich absagen?', gefahr: true });
  }

  return (
    <div className="ta-leiste" aria-label="Turnier-Status">
      <ol className="ta-schritte">
        {ABLAUF.map((s, i) => (
          <li key={s} className={`ta-schritt ${!abgesagt && i < index ? 'ta-erledigt' : ''} ${!abgesagt && i === index ? 'ta-aktuell' : ''}`}>
            <span className="ta-punkt">{!abgesagt && i < index ? '✓' : i + 1}</span>
            <span className="ta-name">{statusText(s)}</span>
          </li>
        ))}
      </ol>
      <div className="ta-zeile">
        <div className="ta-stand">
          {uebung && <span className="ta-uebung">Übung</span>}
          {abgesagt ? <strong className="ta-abgesagt">Abgesagt</strong> : <>Jetzt: <strong>{statusText(wirksam)}</strong></>}
          {fristVorbei && status === TURNIER_STATUS.AKTIV && <span className="ta-klein"> (Anmeldeschluss vorbei)</span>}
          {naechster?.hinweis && <div className="ta-klein">{naechster.hinweis}</div>}
        </div>
        <div className="ta-knoepfe">
          {weitere.map((w) => (
            <button key={w.text} type="button" className={`ta-btn ta-btn-leise ${w.gefahr ? 'ta-btn-gefahr' : ''}`}
              disabled={laeuft} onClick={() => wechseln(w.ziel, w.frage)}>{w.text}</button>
          ))}
          {naechster && (
            <button type="button" className="ta-btn ta-btn-haupt" disabled={laeuft}
              onClick={() => (naechster.nav ? navigate(naechster.nav) : wechseln(naechster.ziel))}>
              {laeuft ? 'Einen Moment …' : naechster.text}
            </button>
          )}
        </div>
      </div>
      {ok && <div className="ta-meldung ta-meldung-ok" role="status">{ok}</div>}
      {fehler && (
        <div className="ta-meldung ta-meldung-fehler" role="alert">
          <strong>{fehler.error}</strong>
          {fehler.fehlend?.length > 0 && (
            <ul>
              {fehler.fehlend.map((p) => (
                <li key={p.id}>{p.titel}: {p.text}{p.wo ? ` (${p.wo})` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
